import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import updateLocale from "dayjs/plugin/updateLocale";
import weekday from "dayjs/plugin/weekday";
import React from "react";

import { ToastProvider } from "@/modules/common/lib/toast";

import { AppQueryClient } from "./appQueryClient";
import { config } from "./config";
import { AuthProvider } from "./modules/auth/AuthContext";
import { ErrorFallback } from "./modules/common/components/ErrorFallback";
import I18nProvider from "./modules/common/components/I18nProvider";
import AppRouter from "./router/AppRouter";
import getTheme, { useThemeModeStore } from "./theme";

dayjs.extend(isBetween);
dayjs.extend(weekday);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
  weekStart: 1,
});
dayjs.locale("en");

type CustomSentryEventHint = Sentry.EventHint & {
  meta?: {
    skipSentry?: boolean;
  };
};

Sentry.init({
  dsn: config.sentry.dsn,
  environment: config.enviroment,
  enabled: !!config.sentry.dsn,
  release: `pinion.adm5@${config.version}`,
  ignoreErrors: [
    /Failed to fetch dynamically imported module/,
    /Non-Error exception captured with keys: message, status/,
  ],
  beforeSend(event, hint) {
    if (
      hint.originalException &&
      (hint.originalException as CustomSentryEventHint).meta?.skipSentry
    ) {
      return null;
    }
    return event;
  },
});

const SentryBoundary = ({ children }: { children: React.ReactNode }) => {
  if (config.enviroment === "local") {
    return <>{children}</>;
  }

  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      {children}
    </Sentry.ErrorBoundary>
  );
};

type AppProps = {
  retry?: number | boolean;
  retryDelay?: number;
};

const App = ({ retry, retryDelay }: AppProps) => {
  const { mode } = useThemeModeStore();
  const theme = React.useMemo(() => {
    const theme = getTheme(mode);
    document.body.style.colorScheme = mode;
    return theme;
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <SentryBoundary>
        <ToastProvider>
          <AppQueryClient retry={retry} retryDelay={retryDelay}>
            <AuthProvider>
              <I18nProvider>
                <CssBaseline />
                <AppRouter />
              </I18nProvider>
            </AuthProvider>
          </AppQueryClient>
        </ToastProvider>
      </SentryBoundary>
    </ThemeProvider>
  );
};
export default App;
